<template>
  <div class="epc-dropdown-container" v-click-outside="closeDropdown">
    <div
      ref="dropdownGroup"
      class="epc-dropdown-button-group flex"
      :class="{ 'active': isDropdownOpen || isActive, 'arrow-hover': isArrowHovered }">
      <div
        :id="buttonId"
        class="epc-button inactive epc-dropdown-main-button"
        :class="{ 'active': isActive || isDropdownOpen }"
        @click="executeSelectedOption">
        <b-icon pack="fas" :icon="(internalSelectedOption && internalSelectedOption.icon) ? internalSelectedOption.icon : (mainIcon || 'object-group')"></b-icon>
      </div>
      <div
        class="epc-button inactive epc-dropdown-arrow-button"
        :class="{ 'active': isDropdownOpen || isActive }"
        @click="toggleDropdown"
        @mouseenter="onArrowHover"
        @mouseleave="onArrowLeave">
        <b-icon pack="fas" icon="chevron-down" :class="{ 'rotate-180': isDropdownOpen }"></b-icon>
      </div>
    </div>
    <div
      v-if="isDropdownOpen"
      class="epc-dropdown-menu"
      :class="{ 'drop-down': dropDown }">
      <div
        v-for="option in options"
        :key="option.value"
        class="epc-dropdown-item"
        :class="{ 'active': internalSelectedOption && internalSelectedOption.value === option.value }"
        @click="selectOption(option)">
        <b-icon pack="fas" :icon="option.icon"></b-icon>
        <span class="ml-2">{{ option.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SplitActionDropdown",

  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedOption: {
      type: Object,
      default: null
    },
    buttonId: {
      type: String,
      default: 'toolbar-dropdown'
    },
    isActive: {
      type: Boolean,
      default: false
    },
    mainIcon: {
      type: String,
      default: null
    },
    defaultOption: {
      type: Object,
      default: null
    },
    // true → el menú se despliega hacia ABAJO (toolbar arriba, estilo Excalidraw).
    // false (default) → hacia arriba (toolbar inferior legacy).
    dropDown: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isDropdownOpen: false,
      isArrowHovered: false,
      internalSelectedOption: null
    }
  },

  watch: {
    selectedOption: {
      immediate: true,
      deep: true,
      handler(newOption) {
        if (newOption) {
          this.internalSelectedOption = { ...newOption };
        } else if (this.defaultOption) {
          this.internalSelectedOption = { ...this.defaultOption };
        } else if (this.options && this.options.length > 0) {
          this.internalSelectedOption = { ...this.options[0] };
        }
      }
    },
    options: {
      immediate: true,
      handler(newOptions) {
        if (newOptions && newOptions.length > 0 && !this.internalSelectedOption) {
          this.internalSelectedOption = this.defaultOption ? { ...this.defaultOption } : { ...newOptions[0] };
        }
      }
    }
  },

  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },

    closeDropdown() {
      this.isDropdownOpen = false;
    },

    onArrowHover() {
      this.isArrowHovered = true;
    },

    onArrowLeave() {
      this.isArrowHovered = false;
    },

    executeSelectedOption() {
      if (!this.internalSelectedOption) return;
      this.$emit('option-selected', this.internalSelectedOption);
    },

    selectOption(option) {
      if (!option) return;
      this.internalSelectedOption = { ...option };
      this.isDropdownOpen = false;
      this.$emit('option-selected', option);
    }
  },

  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            if (vnode.context && binding.expression) {
              vnode.context[binding.expression]();
            }
          }
        };
        setTimeout(() => {
          document.body.addEventListener('click', el.clickOutsideEvent);
        }, 0);
      },
      unbind(el) {
        if (el.clickOutsideEvent) {
          document.body.removeEventListener('click', el.clickOutsideEvent);
        }
      }
    }
  }
}
</script>

<style>
.epc-dropdown-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0;
  overflow: visible;
}

.epc-dropdown-button-group {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  /* Borde + border-box → el estado activo/hover solo recolorea, sin crecer la
     caja (evita el "salto" que empujaba a los vecinos). */
  border: 1px solid #e9e9ef;
  box-sizing: border-box;
  border-radius: 8px;
}

/* Botones del dropdown compactos y alineados a los 30×30 del toolbar (sin los
   márgenes de 4px que lo hacían más grande que sus vecinos). */
.epc-dropdown-button-group .epc-button {
  margin: 0;
  border: 1px solid transparent;
  box-sizing: border-box;
  color: #495057;
}

.epc-dropdown-button-group.active {
  border-color: #c9d6ef;
}

.epc-dropdown-button-group.active .epc-button {
  color: #3a6bc8;
}

.epc-dropdown-button-group.active .epc-dropdown-main-button {
  background: #e8eef8;
  border-right-color: #c9d6ef;
}

.epc-dropdown-button-group.active .epc-dropdown-arrow-button {
  border-left-color: #c9d6ef;
}

.epc-dropdown-main-button {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin: 0;
  width: 30px;
  height: 30px;
  border-right: 1px solid #e9e9ef;   /* divisor entre botón y flecha */
  display: flex;
  align-items: center;
  justify-content: center;
}

.epc-dropdown-arrow-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin: 0;
  padding: 0;
  min-width: 16px;
  width: 16px;
  height: 30px;
  border-left: 1px solid #e9e9ef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Hover sobre la flecha → resalta todo el grupo (azul suave). `:has` + clase
   `.arrow-hover` (fallback JS para navegadores sin :has). */
.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover),
.epc-dropdown-button-group.arrow-hover {
  border-color: #c9d6ef;
}

.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) .epc-button,
.epc-dropdown-button-group.arrow-hover .epc-button {
  color: #3a6bc8;
}

.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) .epc-dropdown-main-button,
.epc-dropdown-button-group.arrow-hover .epc-dropdown-main-button {
  border-right-color: #c9d6ef;
}

.epc-dropdown-button-group:has(.epc-dropdown-arrow-button:hover) .epc-dropdown-arrow-button,
.epc-dropdown-button-group.arrow-hover .epc-dropdown-arrow-button {
  border-left-color: #c9d6ef;
}

.epc-dropdown-arrow-button .icon {
  transition: transform 0.2s ease;
  font-size: 10px;
  width: 10px;
  height: 10px;
}

.epc-dropdown-arrow-button .icon.rotate-180 {
  transform: rotate(180deg);
}

.epc-dropdown-menu {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e9e9ef;
  box-shadow: 0 6px 18px 0 rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  min-width: 150px;
  z-index: 1000;
  overflow: hidden;
  pointer-events: all;
}

/* Despliegue hacia abajo (toolbar arriba, estilo Excalidraw). */
.epc-dropdown-menu.drop-down {
  bottom: auto;
  top: calc(100% + 4px);
}

.epc-dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: #495057;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.epc-dropdown-item:hover {
  background: #f2f2f7;
  color: #3a6bc8;
}

.epc-dropdown-item.active {
  background: #e8eef8;
  color: #3a6bc8;
}

.epc-dropdown-item .icon {
  width: 14px;
  height: 14px;
}

.epc-button {
  border: 1px solid transparent;
  border-radius: 7px;
  cursor: pointer;
  margin: 0;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Hover de un botón del dropdown → fondo e icono en azul de marca (sin tocar el
   borde → no hay salto; el divisor y el borde del grupo ya están reservados). */
.epc-dropdown-button-group .epc-button:hover {
  background: #f2f2f7;
  color: #3a6bc8;
}
</style>
